import React, { useState } from 'react';
import { Platform, ScrollView, TouchableOpacity, View } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Toggle from '../../../../components/Toggle';
import ChevronChico from '../../../../components/ChevronChico';
import Header from '../../../../navigation/Header';
import { Illustration, IllustrationName } from '../../../../components/Illustration';
import useCommunitySettings from '../../../../hooks/useCommunitySettings';
import { QR_CATEGORIES, QRCategoryConfig, QRCategoryKey } from './QRCategoryConfigScreen';


const CATEGORY_CARDS: Array<{ key: QRCategoryKey; illustration: IllustrationName }> = [
  { key: 'personal', illustration: 'gpPersonal' },
  { key: 'provider', illustration: 'gpProveedor' },
  { key: 'meeting',  illustration: 'gpReunion' },
  { key: 'delivery', illustration: 'gpPaqueteria' },
];


function PersonalAccessCard({ title, description, value, onValueChange, disabled }: any) {
  return (
    <View style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`}>
      <View style={tw`row items-center justify-between`}>
        <Text style={tw`text-secondary font-600 text-base flex-1 mr-3`}>{title}</Text>
        <Toggle value={value} onValueChange={onValueChange} disabled={disabled} />
      </View>
      {!!description && <Text style={tw`text-gray-500 text-sm mt-2`}>{description}</Text>}
    </View>
  )
}


export default function QRAccessSettingsScreen({ navigation }) {
  const { updateSettings, getSettingValue, isUpdating } = useCommunitySettings()

  const [contentWidth, setContentWidth] = useState(0)
  const [selected, setSelected] = useState<QRCategoryKey>('personal')

  const isWeb = Platform.OS === 'web'
  const multiColumn = isWeb && contentWidth >= 720

  const allowedTypes = getSettingValue('guest_passes.allowed_types', ['personal', 'meeting']) as string[]

  const onCategoryPress = (key: QRCategoryKey) => {
    if (multiColumn) setSelected(key)
    else navigation.navigate('Admin.Settings.QRCategory', { category: key })
  }

  const categoriesBlock = (
    <View style={tw`mb-6`}>
      <Text style={tw`text-secondary font-600 text-lg mb-1`}>Categorías</Text>
      <Text style={tw`text-gray-500 text-sm mb-4`}>
        Elige los tipos de Códigos de acceso QR que los residentes pueden generar.
      </Text>

      {CATEGORY_CARDS.map(cat => {
        const meta = QR_CATEGORIES[cat.key]
        const enabled = allowedTypes.includes(cat.key)
        const isSelected = multiColumn && selected === cat.key
        return (
          <TouchableOpacity
            key={cat.key}
            style={tw.style(`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3 row items-center`, isSelected && `border-primary`)}
            onPress={() => onCategoryPress(cat.key)}
          >
            <Illustration name={cat.illustration} width={40} height={40} style={tw`mr-4`} />

            <View style={tw`flex-1`}>
              <Text style={tw`text-secondary font-600`}>{meta.title}</Text>
              <Text style={tw`text-gray-500 text-sm`}>{meta.subtitle}</Text>
              <Text style={tw.style(`text-sm font-500 mt-0.5`, enabled ? `text-primary` : `text-gray-400`)}>
                {enabled ? 'Habilitado' : 'Deshabilitado'}
              </Text>
            </View>

            <ChevronChico />
          </TouchableOpacity>
        )
      })}
    </View>
  )

  const accesosBlock = (
    <View style={tw`mb-6`}>
      <View style={tw`row items-center justify-between mb-3`}>
        <Text style={tw`text-secondary font-600 text-lg`}>Accesos personales</Text>
        <View style={tw`w-6 h-6 rounded-full border border-gray-300 items-center justify-center`}>
          <Text style={tw`text-gray-400 text-xs font-600`}>?</Text>
        </View>
      </View>

      <PersonalAccessCard
        title="QR dinámico"
        description="El residente puede utilizar su código dinámico para acceder al fraccionamiento. Aplica para Hardware de acceso y app de vigilante."
        value={getSettingValue('qr.dynamic_enabled', false)}
        onValueChange={(v: boolean) => updateSettings('qr.dynamic_enabled', v)}
        disabled={isUpdating}
      />

      <PersonalAccessCard
        title="Escanea para ingresar"
        description="Para el acceso a las amenidades del fraccionamiento, el residente debe escanear un código QR en la entrada. Los residentes deben estar al corriente para que se les permita el acceso."
        value={getSettingValue('qr.scan_to_enter', false)}
        onValueChange={(v: boolean) => updateSettings('qr.scan_to_enter', v)}
        disabled={isUpdating}
      />
    </View>
  )

  const configBlock = (
    <View>
      <Text style={tw`text-secondary font-600 text-lg mb-4`}>{QR_CATEGORIES[selected].configTitle}</Text>
      <QRCategoryConfig category={selected} />
    </View>
  )

  return (
    <View style={tw`flex-1 bg-background`}>
      <Header showBackButton title="Acceso QR" style="bg-transparent" />

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`px-4 pt-4 pb-10`}>
        <View onLayout={e => setContentWidth(e.nativeEvent.layout.width)}>
          {
            multiColumn ? (
              // Web ancho: maestro (categorías + accesos personales debajo) · detalle (configuración)
              <View style={tw`flex-row`}>
                <View style={tw`flex-1 mr-2`}>
                  {categoriesBlock}
                  {accesosBlock}
                </View>
                <View style={tw.style(`ml-2`, { flex: 1.3 })}>{configBlock}</View>
              </View>
            ) : (
              // Angosto / nativo: una columna (las tarjetas navegan a su pantalla)
              <>
                {categoriesBlock}
                {accesosBlock}
              </>
            )
          }
        </View>
      </ScrollView>
    </View>
  );
}
